import { GOOGLE_PLACES_API_KEY } from "@/config/google";
import { Coordinate } from "@/lib/types/driver-location";
import { useLocationStore } from "@/stores/use-location-store";
import * as Location from "expo-location";
import { Platform } from "react-native";

export const PASSENGER_LOCATION_TTL_MS = 5 * 60 * 1000;

export type PassengerLocationResult = {
  latitude: number;
  longitude: number;
  address: string | null;
  permissionDenied?: boolean;
};

export function formatCoordinateAddress(
  latitude: number,
  longitude: number,
): string {
  return `${latitude.toFixed(3)}, ${longitude.toFixed(3)}`;
}

export function isPassengerLocationFresh(resolvedAt: number | null): boolean {
  if (!resolvedAt) {
    return false;
  }

  return Date.now() - resolvedAt < PASSENGER_LOCATION_TTL_MS;
}

export async function reverseGeocodePassengerLocation(
  latitude: number,
  longitude: number,
): Promise<string | null> {
  try {
    if (Platform.OS === "android") {
      const [address] = await Location.reverseGeocodeAsync({
        latitude,
        longitude,
      });

      if (!address) {
        return null;
      }

      return (
        (address.formattedAddress as string | undefined) ||
        [address.street, address.city].filter(Boolean).join(", ")
      );
    }

    const response = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${GOOGLE_PLACES_API_KEY}`,
    );
    const data = await response.json();

    if (data.status === "OK" && data.results.length > 0) {
      const result = data.results[0];
      const getComp = (type: string) =>
        result.address_components.find((c: { types: string[] }) =>
          c.types.includes(type),
        )?.long_name || "";

      return (
        result.formatted_address ||
        `${getComp("route")}, ${getComp("locality") || getComp("postal_town")}`
      );
    }

    return null;
  } catch (error) {
    console.warn("Unable to reverse geocode passenger location:", error);
    return null;
  }
}

function toCoordinate(latitude: number, longitude: number): Coordinate {
  return { latitude, longitude, heading: null, speed: 0 };
}

function cachePassengerLocation(
  latitude: number,
  longitude: number,
  address: string | null,
): void {
  useLocationStore.getState().setPassengerLocation({
    coordinate: toCoordinate(latitude, longitude),
    resolvedAddress: address,
    resolvedAt: Date.now(),
  });
}

export function getFreshPassengerLocation(): {
  latitude: number;
  longitude: number;
  address: string | null;
} | null {
  const { coordinate, resolvedAddress, resolvedAt } =
    useLocationStore.getState();

  if (
    !isPassengerLocationFresh(resolvedAt) ||
    !coordinate?.latitude ||
    !coordinate?.longitude
  ) {
    return null;
  }

  return {
    latitude: coordinate.latitude,
    longitude: coordinate.longitude,
    address: resolvedAddress,
  };
}

type ResolveOptions = {
  /** Called as soon as a seed coordinate is available (cache / last-known / final). */
  onSeed?: (latitude: number, longitude: number, address: string | null) => void;
  /** Skip using the short-lived address cache (locate button / quiet refresh). */
  forceRefresh?: boolean;
};

let inFlightResolve: Promise<PassengerLocationResult | null> | null = null;

function notifySeed(
  onSeed: ResolveOptions["onSeed"],
  latitude: number,
  longitude: number,
  address: string | null,
): void {
  onSeed?.(latitude, longitude, address);
}

export async function resolvePassengerLocationCoords(
  options: ResolveOptions = {},
): Promise<PassengerLocationResult | null> {
  if (!options.forceRefresh && inFlightResolve) {
    const result = await inFlightResolve;
    if (result && !result.permissionDenied) {
      notifySeed(
        options.onSeed,
        result.latitude,
        result.longitude,
        result.address,
      );
    }
    return result;
  }

  const run = async (): Promise<PassengerLocationResult | null> => {
    try {
      if (!options.forceRefresh) {
        const fresh = getFreshPassengerLocation();
        if (fresh) {
          notifySeed(
            options.onSeed,
            fresh.latitude,
            fresh.longitude,
            fresh.address,
          );
          return fresh;
        }
      }

      const { status: existingStatus } =
        await Location.getForegroundPermissionsAsync();
      let status = existingStatus;

      if (status !== "granted") {
        ({ status } = await Location.requestForegroundPermissionsAsync());
      }

      if (status !== "granted") {
        return {
          latitude: 0,
          longitude: 0,
          address: null,
          permissionDenied: true,
        };
      }

      const seedFromCoordinates = (
        latitude: number,
        longitude: number,
        address: string | null = null,
      ) => {
        useLocationStore
          .getState()
          .setCoordinate(toCoordinate(latitude, longitude));
        notifySeed(options.onSeed, latitude, longitude, address);

        if (!address) {
          void reverseGeocodePassengerLocation(latitude, longitude).then(
            (resolved) => {
              if (!resolved) {
                return;
              }

              cachePassengerLocation(latitude, longitude, resolved);
              notifySeed(options.onSeed, latitude, longitude, resolved);
            },
          );
        }
      };

      const { coordinate: cachedCoordinate, resolvedAddress, resolvedAt } =
        useLocationStore.getState();

      if (cachedCoordinate?.latitude && cachedCoordinate?.longitude) {
        seedFromCoordinates(
          cachedCoordinate.latitude,
          cachedCoordinate.longitude,
          isPassengerLocationFresh(resolvedAt) ? resolvedAddress : null,
        );
      } else {
        const lastKnown = await Location.getLastKnownPositionAsync({
          maxAge: PASSENGER_LOCATION_TTL_MS,
        });

        if (lastKnown) {
          seedFromCoordinates(
            lastKnown.coords.latitude,
            lastKnown.coords.longitude,
          );
        }
      }

      const currentPosition = await Location.getCurrentPositionAsync({
        accuracy: Location.Accuracy.Balanced,
      });

      const { latitude, longitude } = currentPosition.coords;
      const address = await reverseGeocodePassengerLocation(
        latitude,
        longitude,
      );

      cachePassengerLocation(latitude, longitude, address);
      notifySeed(options.onSeed, latitude, longitude, address);

      return { latitude, longitude, address };
    } catch (error) {
      console.error("Failed to fetch passenger location:", error);
      return null;
    } finally {
      if (!options.forceRefresh) {
        inFlightResolve = null;
      }
    }
  };

  const promise = run();
  if (!options.forceRefresh) {
    inFlightResolve = promise;
  }
  return promise;
}

export async function prefetchPassengerLocation(): Promise<void> {
  try {
    await resolvePassengerLocationCoords();
  } catch (error) {
    console.warn("Passenger location prefetch failed:", error);
  }
}
